/* eslint-disable @atlaskit/design-system/icon-label */
import React, { FC, useCallback, useState } from 'react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import ArrowRight from '@atlaskit/icon/glyph/arrow-right';
import EditIcon from '@atlaskit/icon/glyph/edit';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import CreditcardIcon from '@atlaskit/icon/glyph/creditcard';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import { AtlassianIcon } from '@atlaskit/logo';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import { SloDTO } from '@atlassiansox/phobos-client-ts';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import JiraTestSessionIcon from '@atlaskit/icon/glyph/jira/test-session';

import { useFeatureGatesController, useLocationController } from '@/controllers';
import { MoreButton } from '@/components/primitive';
import {
  callbackViewInSignalFxButtonClickedEvent,
  ViewInSignalFxButtonClickedEventPageSource,
} from '@/common/ufo/experiences';

import {
  useActions,
  useCreateTempBurnRateGraphAction,
  useCreateTempErrorBudgetGraphAction,
} from '../actions';
import DebugModal from '../debug-modal';

import { testIds } from './constants';

type SloMenuProps = {
  isSkeleton?: boolean;
  publicId: string;
  indicator?: SloDTO['indicator'];
  parentCapabilityPublicId?: string;
  name: string;
  // This can be used by SLO cards on team / capability page
  isGroupCard?: boolean;
};

const SloMenu: FC<SloMenuProps> = ({
  isSkeleton,
  indicator,
  publicId,
  parentCapabilityPublicId,
  name,
  isGroupCard,
}) => {
  const { allowSLODebug } = useFeatureGatesController();
  const { isSLOGroupPage, isSLOGroupMemberPage } = useLocationController();
  const navigate = useNavigate();

  const onDeleteSuccess = () => {
    if (parentCapabilityPublicId) {
      navigate(`/capability/${parentCapabilityPublicId}`);
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [sfxMenuIsOpen, setSfxMenuIsOpen] = useState(false);

  const { onCopy, onEdit, onDelete } = useActions(publicId, name, 'slo', onDeleteSuccess);
  const { onCreateTempErrorBudgetChart } = useCreateTempErrorBudgetGraphAction(publicId);
  const { onCreateTempBurnRateChart } = useCreateTempBurnRateGraphAction(publicId);
  const {
    onViewErrorBudgetInSignalFxButtonClicked,
    onViewBurnRatesInSignalFxButtonClicked,
  } = callbackViewInSignalFxButtonClickedEvent(
    ViewInSignalFxButtonClickedEventPageSource.SloPage,
    publicId
  );

  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const onDebug = useCallback(() => {
    setIsDebugModalOpen(true);
  }, []);
  const handleSetDebugModalOpen = useCallback((b: boolean) => {
    setIsDebugModalOpen(b);
  }, []);

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
        setSfxMenuIsOpen(false);
      }}
      trigger={triggerProps => (
        <MoreButton
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...triggerProps}
          isDisabled={isSkeleton}
          onClick={() => {
            setIsOpen(!isOpen);
            setSfxMenuIsOpen(false);
          }}
        />
      )}
      placement="bottom-start"
      content={() => (
        <MenuGroup onClick={e => e.stopPropagation()}>
          <Section>
            {(() => {
              const perfPortalUrl = indicator?.uiMetrics?.population?.perfPortalUrl;

              if (!perfPortalUrl) return null;

              return (
                <DropdownItem
                  href={perfPortalUrl}
                  target="_blank"
                  elemBefore={<AtlassianIcon size="xsmall" label="Copy" />}
                >
                  View in Performance Portal
                </DropdownItem>
              );
            })()}
            <ButtonItem iconBefore={<CopyIcon size="small" label="" />} onClick={onCopy}>
              Copy ID
            </ButtonItem>
            {!isGroupCard && !isSLOGroupPage && indicator?.goodQuery && indicator?.badQuery && (
              <Popup
                isOpen={sfxMenuIsOpen}
                onClose={() => setSfxMenuIsOpen(false)}
                placement="right-start"
                trigger={triggerProps => (
                  <ButtonItem
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...triggerProps}
                    iconBefore={<GraphLineIcon size="small" label="" />}
                    onClick={() => setSfxMenuIsOpen(!sfxMenuIsOpen)}
                    iconAfter={<ArrowRight size="small" label="" />}
                    isSelected={sfxMenuIsOpen}
                    data-testid={testIds.createSignalFxChartButton}
                  >
                    View in SignalFX
                  </ButtonItem>
                )}
                content={() => (
                  <MenuGroup onClick={e => e.stopPropagation()}>
                    <Section>
                      <ButtonItem
                        iconBefore={<CreditcardIcon size="small" label="" />}
                        data-testid={testIds.createErrorBudgetButton}
                        onClick={() => {
                          onViewErrorBudgetInSignalFxButtonClicked();
                          onCreateTempErrorBudgetChart();
                        }}
                      >
                        Chart With Error Budget
                      </ButtonItem>
                      <ButtonItem
                        iconBefore={<FilterIcon size="small" label="" />}
                        onClick={() => {
                          onViewBurnRatesInSignalFxButtonClicked();
                          onCreateTempBurnRateChart();
                        }}
                        data-testid={testIds.createBurnRateButton}
                      >
                        Chart With Burn Rates
                      </ButtonItem>
                    </Section>
                  </MenuGroup>
                )}
              />
            )}
            {!isGroupCard && !isSLOGroupPage && (
              <>
                <ButtonItem
                  iconBefore={
                    isSLOGroupMemberPage ? (
                      <SettingsIcon size="small" label="Edit" />
                    ) : (
                      <EditIcon size="small" label="Edit" />
                    )
                  }
                  onClick={() => {
                    onEdit();
                  }}
                >
                  {isSLOGroupMemberPage ? 'View Config' : 'Edit'}
                </ButtonItem>
                {!isSLOGroupMemberPage && (
                  <ButtonItem
                    iconBefore={<TrashIcon size="small" label="Delete" />}
                    onClick={onDelete}
                  >
                    Delete
                  </ButtonItem>
                )}
              </>
            )}
            {allowSLODebug && (
              <ButtonItem
                iconBefore={<JiraTestSessionIcon size="small" label="Debug" />}
                onClick={onDebug}
              >
                <span>Debug</span>
                <DebugModal
                  id={publicId}
                  isOpen={isDebugModalOpen}
                  setIsOpen={handleSetDebugModalOpen}
                />
              </ButtonItem>
            )}
          </Section>
        </MenuGroup>
      )}
    />
  );
};

export default SloMenu;
